// components/ProjectsCarousel.tsx
"use client";

import * as React from "react";
import Link from "next/link";
import { IconArrowRight, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import type { Project } from "@/constants/projects";
import ProjectCard from "@/components/projectCard";
import SectionHeading from "@/components/home/SectionHeading";

interface ProjectsCarouselProps {
  projects: Project[];
  autoPlayMs?: number;
  onCardClick?: (project: Project) => void;
}

// Slides are positioned relative to the track (it is `relative`), minus the track's left padding
const slideLeft = (track: HTMLElement, slide: HTMLElement) =>
  slide.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || "0");

const arrowClass =
  "flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-[#0E1018] text-white transition-colors hover:border-white/25 hover:bg-[#161a26]";

export default function ProjectsCarousel({ projects, autoPlayMs = 4000, onCardClick }: ProjectsCarouselProps) {
  const trackRef = React.useRef<HTMLDivElement | null>(null);
  const [isPaused, setIsPaused] = React.useState(false);
  const [active, setActive] = React.useState(0);

  const goToIndex = React.useCallback(
    (idx: number) => {
      const track = trackRef.current;
      if (!track) return;
      const slides = Array.from(track.children) as HTMLElement[];
      if (!slides.length) return;

      const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
      let target = idx;
      if (idx >= slides.length || (atEnd && idx > active)) target = 0; // wrap forward
      if (idx < 0) target = slides.length - 1; // wrap back

      track.scrollTo({ left: slideLeft(track, slides[target]), behavior: "smooth" });
    },
    [active]
  );

  // Track which slide is at the left edge, for the dots
  React.useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const slides = Array.from(track.children) as HTMLElement[];
      const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
      if (atEnd) return setActive(slides.length - 1);
      let best = 0;
      slides.forEach((slide, i) => {
        const d = Math.abs(slideLeft(track, slide) - track.scrollLeft);
        if (d < Math.abs(slideLeft(track, slides[best]) - track.scrollLeft)) best = i;
      });
      setActive(best);
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => track.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    if (!autoPlayMs || isPaused) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => goToIndex(active + 1), autoPlayMs);
    return () => clearInterval(id);
  }, [autoPlayMs, isPaused, goToIndex, active]);

  React.useEffect(() => {
    const onVisibility = () => setIsPaused(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  return (
    <section
      className="rounded-[20px] border border-white/[0.06] bg-black p-5 sm:p-6 md:p-10"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      <SectionHeading
        eyebrow="Portfolio"
        title="Selected work"
        subtitle="Configurators, management systems, dashboards and AI products shipped for real clients."
        action={
          <div className="flex w-full items-center gap-2 md:w-auto">
            <button type="button" aria-label="Previous project" onClick={() => goToIndex(active - 1)} className={arrowClass}>
              <IconChevronLeft size={20} />
            </button>
            <button type="button" aria-label="Next project" onClick={() => goToIndex(active + 1)} className={arrowClass}>
              <IconChevronRight size={20} />
            </button>
            <Link
              href="/projects"
              className="group ml-auto inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-full bg-white px-5 md:ml-2 text-[15px] font-bold text-black transition-colors hover:bg-white/90"
            >
              View all
              <IconArrowRight size={17} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        }
      />

      <div
        ref={trackRef}
        className="no-scrollbar relative -mx-2 mt-6 flex md:mt-8 snap-x snap-mandatory scroll-px-2 gap-5 overflow-x-auto scroll-smooth px-2 pb-2 pt-2"
        aria-label="Projects carousel"
        aria-roledescription="carousel"
      >
        {projects.map((project, i) => (
          <div
            key={project.id}
            className="shrink-0 basis-[90%] snap-start sm:basis-[60%] md:basis-[48%] lg:basis-[32%]"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${projects.length}`}
          >
            <ProjectCard project={project} tone="raised" onClick={() => onCardClick?.(project)} />
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-center gap-1.5" aria-hidden>
        {projects.map((p, i) => (
          <button
            key={p.id}
            type="button"
            tabIndex={-1}
            onClick={() => goToIndex(i)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === active ? "w-6 bg-[#48AEEF]" : "w-1.5 bg-white/20 hover:bg-white/40"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
